/**
 * 판정 기록 목록 필터 바 (페이지 로컬 — DoseRules JudgmentBar 선례).
 *
 * @spec:2026-09-29-처방검토-판정기록-조회화면
 *
 * 값·콜백만. 텍스트(약국·룰·약품)는 입력 중 로컬 상태 → Enter·blur에 커밋(URL 반영은 페이지).
 * select·체크박스·기간은 즉시 커밋. 날짜는 로컬 Date로 넘긴다(문자열 금지 — new Date("YYYY-MM-DD") UTC 파싱 함정).
 */
import React, { useEffect, useState } from "react";
import { DateRangePicker } from "components/molecules/DateRangePicker";
import { formatDate } from "utils/DateUtil";
import {
  GRADE_FILTER_OPTIONS,
  SOURCE_FILTER_OPTIONS,
  LABEL_STATE_OPTIONS,
  ALERT_ONLY_LABEL,
  DOSE_ALERT_ONLY_LABEL,
  RULE_SCOPE_APPLIED_LABEL,
  PHARMACY_INPUT_HINT,
  RULE_INPUT_HINT,
  SOURCE_UNKNOWN_TOOLTIP,
  type DoseReviewLogFilterState,
} from "utils/doseReviewLogs";
import type { DoseGrade, DoseLabelState, DoseReviewLogSource } from "services/api/doseReviewLogs/types";
import { Bar, Field, FieldLabel, Hint, ErrorMsg, Checks, CheckLabel, TextInput, Select } from "./filterStyle";

const ymdToLocalDate = (ymd: string): Date => {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

export interface FilterBarProps {
  filters: DoseReviewLogFilterState;
  periodMessage?: string;
  productMessage?: string;
  onCommit: (patch: Partial<DoseReviewLogFilterState>) => void;
}

const FilterBar: React.FC<FilterBarProps> = ({ filters, periodMessage, productMessage, onCommit }) => {
  const [pharmacy, setPharmacy] = useState(filters.pharmacyUserId);
  const [rule, setRule] = useState(filters.ruleId);
  const [product, setProduct] = useState(filters.product);

  // 외부에서 커밋된 값(상위 룰 클릭·필터 초기화)이 바뀌면 입력을 다시 맞춘다.
  useEffect(() => setPharmacy(filters.pharmacyUserId), [filters.pharmacyUserId]);
  useEffect(() => setRule(filters.ruleId), [filters.ruleId]);
  useEffect(() => setProduct(filters.product), [filters.product]);

  const toggleGrade = (g: DoseGrade) => {
    const next = filters.grades.includes(g)
      ? filters.grades.filter((x) => x !== g)
      : [...filters.grades, g];
    onCommit({ grades: next });
  };

  return (
    <Bar data-testid="dose-log-filterbar">
      <Field>
        <FieldLabel>기간</FieldLabel>
        <DateRangePicker
          startDate={ymdToLocalDate(filters.start)}
          endDate={ymdToLocalDate(filters.end)}
          disabled={false}
          error={!!periodMessage}
          onChangeDate={(name, date) =>
            onCommit(name === "start" ? { start: formatDate(date) } : { end: formatDate(date) })
          }
        />
        {periodMessage && <ErrorMsg data-testid="dose-log-period-error">{periodMessage}</ErrorMsg>}
      </Field>

      <Field>
        <FieldLabel>출처</FieldLabel>
        <Select
          data-testid="dose-log-source"
          value={filters.source ?? ""}
          onChange={(e) =>
            onCommit({ source: (e.target.value || undefined) as DoseReviewLogSource | undefined })
          }
        >
          {SOURCE_FILTER_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
        <Hint title={SOURCE_UNKNOWN_TOOLTIP}>미상 ⓘ</Hint>
      </Field>

      <Field>
        <FieldLabel>약국</FieldLabel>
        <TextInput
          data-testid="dose-log-pharmacy"
          value={pharmacy}
          onChange={(e) => setPharmacy(e.target.value)}
          onBlur={() => onCommit({ pharmacyUserId: pharmacy })}
          onKeyDown={(e) => e.key === "Enter" && onCommit({ pharmacyUserId: pharmacy })}
        />
        <Hint>{PHARMACY_INPUT_HINT}</Hint>
      </Field>

      <Field>
        <FieldLabel>룰</FieldLabel>
        <TextInput
          data-testid="dose-log-rule"
          value={rule}
          onChange={(e) => setRule(e.target.value)}
          onBlur={() => onCommit({ ruleId: rule })}
          onKeyDown={(e) => e.key === "Enter" && onCommit({ ruleId: rule })}
        />
        <Hint>{RULE_INPUT_HINT}</Hint>
        <CheckLabel>
          <input
            type="checkbox"
            data-testid="dose-log-rulescope"
            checked={filters.ruleScope === "applied"}
            onChange={(e) => onCommit({ ruleScope: e.target.checked ? "applied" : "alert" })}
          />
          {RULE_SCOPE_APPLIED_LABEL}
        </CheckLabel>
      </Field>

      <Field>
        <FieldLabel>등급</FieldLabel>
        <Checks>
          {GRADE_FILTER_OPTIONS.map((o) => (
            <CheckLabel key={o.value}>
              <input
                type="checkbox"
                data-testid={`dose-log-grade-${o.value}`}
                checked={filters.grades.includes(o.value)}
                onChange={() => toggleGrade(o.value)}
              />
              {o.label}
            </CheckLabel>
          ))}
        </Checks>
      </Field>

      <Field>
        <Checks>
          <CheckLabel>
            <input
              type="checkbox"
              data-testid="dose-log-alertonly"
              checked={filters.alertOnly}
              onChange={(e) => onCommit({ alertOnly: e.target.checked })}
            />
            {ALERT_ONLY_LABEL}
          </CheckLabel>
          <CheckLabel>
            <input
              type="checkbox"
              data-testid="dose-log-dosealertonly"
              checked={filters.doseAlertOnly}
              onChange={(e) => onCommit({ doseAlertOnly: e.target.checked })}
            />
            {DOSE_ALERT_ONLY_LABEL}
          </CheckLabel>
        </Checks>
      </Field>

      <Field>
        <FieldLabel>정오 표기 상태</FieldLabel>
        <Select
          data-testid="dose-log-labelstate"
          value={filters.labelState}
          onChange={(e) => onCommit({ labelState: e.target.value as DoseLabelState })}
        >
          {LABEL_STATE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      </Field>

      <Field>
        <FieldLabel>약품</FieldLabel>
        <TextInput
          data-testid="dose-log-product"
          value={product}
          onChange={(e) => setProduct(e.target.value)}
          onBlur={() => onCommit({ product })}
          onKeyDown={(e) => e.key === "Enter" && onCommit({ product })}
        />
        {productMessage && <ErrorMsg data-testid="dose-log-product-error">{productMessage}</ErrorMsg>}
      </Field>
    </Bar>
  );
};

export default FilterBar;
